import { useState, useEffect } from 'react';
import { X, Search, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { planningApi } from '../../api/planning';
import apiClient from '../../api/client';

export default function CreateScenarioDialog({ open, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [opportunities, setOpportunities] = useState([]);
  const [opportunitiesLoading, setOpportunitiesLoading] = useState(false);
  const [parts, setParts] = useState([]);
  const [partsLoading, setPartsLoading] = useState(false);
  const [searchParts, setSearchParts] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    opportunity_id: '',
    scope_type: 'multiple_parts',  // Default to multiple parts
    affected_part_ids: []
  });
  
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      fetchOpportunities();
    }
  }, [open]);

  useEffect(() => {
    if (formData.opportunity_id) {
      fetchParts();
    }
  }, [formData.opportunity_id]);

  const fetchOpportunities = async () => {
    setOpportunitiesLoading(true);
    try {
      const response = await apiClient.get('/api/v1/pipeline/opportunities');
      
      if (response.data.success && response.data.data && response.data.data.stages) {
        // Flatten all opportunities from all stages
        const stages = response.data.data.stages;
        const allOpportunities = Object.values(stages).flat();
        setOpportunities(allOpportunities);
      }
    } catch (error) {
      console.error('Failed to fetch opportunities:', error);
    } finally {
      setOpportunitiesLoading(false);
    }
  };

  const fetchParts = async () => {
    setPartsLoading(true);
    try {
      const response = await apiClient.get(`/api/v1/pipeline/opportunities/${formData.opportunity_id}/parts`);
      
      if (response.data.success && response.data.data) {
        const partsList = response.data.data;
        setParts(partsList);
        
        // Clear any existing selections when switching opportunities
        setFormData(prev => ({
          ...prev,
          affected_part_ids: []
        }));
      }
    } catch (error) {
      console.error('Failed to fetch parts:', error);
      setParts([]);
    } finally {
      setPartsLoading(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      // Select all parts - use part_id from API response
      const allPartIds = parts
        .filter(p => p && p.part_id)
        .map(p => String(p.part_id));
      setFormData(prev => ({
        ...prev,
        affected_part_ids: allPartIds
      }));
    } else {
      // Deselect all parts
      setFormData(prev => ({
        ...prev,
        affected_part_ids: []
      }));
    }
  };

  const togglePart = (partId) => {
    const partIdStr = String(partId);
    
    const currentSelection = [...formData.affected_part_ids];
    const isCurrentlySelected = currentSelection.includes(partIdStr);
    
    let newSelection;
    if (isCurrentlySelected) {
      // Remove this part
      newSelection = currentSelection.filter(id => id !== partIdStr);
    } else {
      // Add this part
      newSelection = [...currentSelection, partIdStr];
    }
    
    setFormData(prevState => ({
      ...prevState,
      affected_part_ids: newSelection
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name || !formData.opportunity_id) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.affected_part_ids.length === 0) {
      setError('Please select at least one part');
      return;
    }

    setLoading(true);

    try {
      const result = await planningApi.createScenario({
        ...formData,
        changes: { part_changes: {} }  // Initialize empty changes
      });

      if (result.success) {
        onSuccess(result.data);
        handleClose();
      } else {
        const errorMsg = typeof result.error === 'object' 
          ? JSON.stringify(result.error) 
          : (result.error || 'Failed to create scenario');
        setError(errorMsg);
      }
    } catch (err) {
      console.error('Scenario creation error:', err);
      const errorMsg = typeof err === 'object' 
        ? (err.message || JSON.stringify(err))
        : (err || 'Failed to create scenario');
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      opportunity_id: '',
      scope_type: 'multiple_parts',
      affected_part_ids: []
    });
    setError('');
    setParts([]);
    setSearchParts('');
    onClose();
  };

  const filteredParts = parts.filter(part => {
    if (!searchParts) return true;
    const query = searchParts.toLowerCase();
    return (
      part.part_number?.toLowerCase().includes(query) ||
      part.description?.toLowerCase().includes(query)
    );
  });

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Scenario</DialogTitle>
          <DialogDescription>
            Create a what-if scenario to analyze different strategic options
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Scenario Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Scenario Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Q4 Volume Push, Cost Reduction Initiative"
              required
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the key assumptions and objectives..."
              rows={3}
            />
          </div>

          {/* Opportunity Selection */}
          <div className="space-y-2">
            <Label htmlFor="opportunity">Opportunity *</Label>
            <select
              id="opportunity"
              value={formData.opportunity_id}
              onChange={(e) => setFormData({ ...formData, opportunity_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
            >
              <option value="">Select opportunity...</option>
              {opportunities.map((opp) => (
                <option key={opp.id} value={opp.id}>
                  {opp.program_name}
                </option>
              ))}
            </select>
          </div>

          {/* Parts Selection */}
          {formData.opportunity_id && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Parts Selection ({formData.affected_part_ids.length} selected)</Label>
                {parts.length > 0 && (
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="select-all-parts"
                      checked={parts.length > 0 && formData.affected_part_ids.length === parts.length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="h-4 w-4 text-blue-600 border-blue-500 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="select-all-parts" className="text-sm font-medium text-blue-700 cursor-pointer">
                      Select All
                    </label>
                  </div>
                )}
              </div>
              
              {partsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                </div>
              ) : parts.length === 0 ? (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                  No parts found for this opportunity. Add parts first.
                </div>
              ) : (
                <>
                  {parts.length > 5 && (
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search parts..."
                        value={searchParts}
                        onChange={(e) => setSearchParts(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  )}

                  {/* Parts List */}
                  <div className="border border-gray-300 rounded-md max-h-64 overflow-y-auto">
                    {filteredParts.map((part) => {
                      const partIdStr = String(part.part_id);
                      const isChecked = formData.affected_part_ids.includes(partIdStr);
                      
                      return (
                        <div
                          key={part.part_id}
                          className="flex items-start gap-3 p-3 hover:bg-gray-50 border-b last:border-b-0"
                        >
                          <input
                            type="checkbox"
                            id={`part-${partIdStr}`}
                            checked={isChecked}
                            onChange={() => togglePart(part.part_id)}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-1"
                          />
                          <label htmlFor={`part-${partIdStr}`} className="flex-1 min-w-0 cursor-pointer">
                            <div className="font-medium text-sm">{part.part_number}</div>
                            <div className="text-xs text-gray-600 truncate">{part.description}</div>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !formData.opportunity_id || formData.affected_part_ids.length === 0}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Scenario'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
